import { useLazyQuery, useReactiveVar } from '@apollo/client';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import { useContext, useEffect, useState } from 'react';
import BatchDownloadDialog from 'src/components/dialogs/BatchDownloadDialog';
import { buildSharedDownloadAvailabilityQ } from 'src/dynamoQueries/download';
import { getCollectionItemGlobalIds } from 'src/helpers';
import { useCollectionSimpleQuery } from 'src/hooks/collection';
import { useSharedCollectionBulkDownload } from 'src/hooks/download';
import { SharedLinkContext, type SharedLinkContextValue } from 'src/SharedLinkPage/context';
import viewStateVar from 'src/store/viewState';
import NavLogo from './AkomiLogo/AkomiNavLogo';
import { BaseNavBar } from './TopNavBar';

interface AvailabilityEntry {
  available: number;
  total: number;
}

interface AvailabilityMap {
  [code: string]: AvailabilityEntry;
}

interface FormatOption {
  value: string;
  label: string;
}

interface AvailabilityItem {
  formatType: string;
  formatLabel?: string;
  available: number;
  total: number;
}

interface AvailabilityQueryData {
  sharedDownloadAvailability: AvailabilityItem[];
}

interface CollectionItemNode {
  node: {
    contentObject: {
      id: string;
    };
  };
}

interface CollectionQueryData {
  sharedCollection: {
    items: {
      edges: CollectionItemNode[];
    };
  };
}

export const SharedGalleryTopNavBar: React.FC = () => {
  const classes = sharedGalleryStyles();
  const { linkInfo } = useContext(SharedLinkContext) as SharedLinkContextValue;

  const collectionId = linkInfo.collectionId || '';
  const { startSharedCollectionDownload } = useSharedCollectionBulkDownload(collectionId);
  const viewState = useReactiveVar(viewStateVar);
  const isDialogOpen = Boolean(viewState?.sharedCollectionDownloadDialog);
  const [availabilityMap, setAvailabilityMap] = useState<AvailabilityMap | null>(null);
  const [options, setOptions] = useState<FormatOption[] | null>(null);

  // Lazy shared availability query; cache-first to reduce calls
  const [getAvailability, { loading: availabilityLoading }] = useLazyQuery<AvailabilityQueryData>(
    buildSharedDownloadAvailabilityQ(),
    {
      fetchPolicy: 'cache-first',
      onCompleted: (data) => {
        try {
          const list = data?.sharedDownloadAvailability || [];
          // Build availability map keyed by code (both upper and lower for robustness)
          const map = list.reduce<AvailabilityMap>((acc, item) => {
            const code = String(item.formatType).toUpperCase();
            const entry: AvailabilityEntry = { available: item.available, total: item.total };
            acc[code] = entry;
            acc[code.toLowerCase()] = entry;
            return acc;
          }, {});
          setAvailabilityMap(map);
          // Build dynamic options with labels when available
          const opts: FormatOption[] = list.map((it) => ({
            value: String(it.formatType).toUpperCase(),
            label: it.formatLabel || String(it.formatType).toUpperCase(),
          }));
          setOptions(opts);
        } catch (_e) {
          // Advisory only; safe to ignore
        }
      },
      onError: () => {},
    },
  );

  // Lazy resolver for shared collection items to expand to asset globalIds (cap 100)
  const collectionQueryResult = useCollectionSimpleQuery({
    variables: {
      id: collectionId,
      first: 100,
    },
    options: {
      onCompleted: async (data: CollectionQueryData) => {
        try {
          const itemEdges = data?.sharedCollection?.items?.edges || [];
          const ids = getCollectionItemGlobalIds(itemEdges).slice(0, 100);
          if (ids.length > 0) {
            await getAvailability({ variables: { globalIds: ids } });
          } else {
            setAvailabilityMap({});
            setOptions([]);
          }
        } catch (_e) {
          setAvailabilityMap({});
          setOptions([]);
        }
      },
      fetchPolicy: 'network-only',
    },
    lazy: true,
    shared: true,
  });

  // Type assertion for lazy query result (returns tuple when lazy: true)
  const [getCollection, { loading: collectionLoading }] = collectionQueryResult as unknown as [
    () => void,
    { loading: boolean },
  ];

  const openDownloadDialog = (): void => {
    // Open immediately so the dialog can show a spinner while availability loads
    viewStateVar({ ...viewStateVar(), sharedCollectionDownloadDialog: true });
    getCollection();
  };

  // When dialog is opened from elsewhere (e.g., kebab), fetch availability
  // biome-ignore lint/correctness/useExhaustiveDependencies: getCollection is a lazy query function that is stable, options is intentionally excluded to avoid refetch loops
  useEffect(() => {
    if (isDialogOpen) {
      // Fetch collection items only if not already loaded options
      if (!options || options.length === 0) {
        getCollection();
      }
    }
  }, [isDialogOpen]);

  return (
    <>
      <BaseNavBar>
        <NavLogo />
        <div className={classes.iconContainer}>
          {linkInfo?.downloadable && (
            <Tooltip title="Download Gallery">
              <IconButton
                className={classes.iconButton}
                color="primary"
                aria-label="download gallery"
                onClick={openDownloadDialog}
                size="large"
              >
                <DownloadIcon sx={{ fontSize: 22 }} className={classes.downloadIcon} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </BaseNavBar>
      <BatchDownloadDialog
        isOpen={isDialogOpen}
        availability={availabilityMap}
        options={options}
        loading={availabilityLoading || collectionLoading}
        onConfirm={({ formatType }: { formatType: string | null }) => {
          if (formatType) {
            startSharedCollectionDownload({ formatType, fallbackPolicy: 'SKIP_MISSING' });
          }
          viewStateVar({ ...viewStateVar(), sharedCollectionDownloadDialog: false });
        }}
        onCancel={() => viewStateVar({ ...viewStateVar(), sharedCollectionDownloadDialog: false })}
      />
    </>
  );
};

const sharedGalleryStyles = makeStyles(() => ({
  iconContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    width: '100%',
  },
  iconButton: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    cursor: 'pointer',
  },
  downloadIcon: {
    marginTop: '5px',
  },
  shareIcon: {
    marginRight: '-20px',
  },
}));
